import React, { useEffect, useMemo, useState } from "react";
import {
  AutoComplete,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Segmented,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { DashAntd } from "../components/DashAntd";
import { useDashTheme } from "../hooks/useDashTheme";
import { BlotterCss, KpiCell, LivingAntd, Micro, QuietEmpty, Rule, Sheet, Well } from "./living/ltChrome";

const { Text } = Typography;

const FILE_KIND = {
  audit: "Audit / VAPT",
  scores: "SCORES",
  ckyc: "CKYC / KYC",
  other: "Other",
};

function errLine(err) {
  return (err.response && err.response.data && err.response.data.error && err.response.data.error.message)
    || err.message
    || "Could not save";
}

function prettyDay(iso) {
  if (!iso) return "—";
  const d = dayjs(iso);
  return d.isValid() ? d.format("D MMM") : iso;
}

function prettyMonth(m) {
  if (!m) return "This month";
  const d = dayjs(`${m}-01`);
  return d.isValid() ? d.format("MMMM YYYY") : m;
}

function SideMark({ side }) {
  const t = useDashTheme();
  const buy = String(side).toLowerCase() === "buy";
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      height: 22,
      padding: "0 8px",
      borderRadius: t.r.xs,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      background: buy ? t.c.primarySoft : t.status.warningSoft,
      color: buy ? t.c.primary : t.status.warning,
    }}>
      {buy ? "Buy" : "Sell"}
    </span>
  );
}

function StatusInk({ status }) {
  const t = useDashTheme();
  const ok = status === "cleared";
  return (
    <span style={{
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: ok ? t.status.positive : t.status.negative,
    }}>
      {status}
    </span>
  );
}

function GreyMark() {
  const t = useDashTheme();
  return (
    <Tooltip title="This name is on the grey list">
      <span style={{
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: t.status.negative,
      }}>
        Grey
      </span>
    </Tooltip>
  );
}

export default function LivingToolDesk({ tab, desk, userId, onMutate, canAct }) {
  const t = useDashTheme();
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    if (!canAct) return false;
    setBusy(true);
    try {
      await fn();
      if (ok) message.success(ok);
      return true;
    } catch (err) {
      message.error(errLine(err));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const counts = (desk && desk.counts) || {};

  return (
    <DashAntd>
      <LivingAntd>
        <div className="lt-root">
          <BlotterCss />
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(168px, 1fr))",
            gap: 10,
            marginBottom: 18,
          }}
          >
            <KpiCell label="Pending" value={counts.pending || 0} sub="waiting on you" />
            <KpiCell
              label="On grey list"
              value={counts.greyHits || 0}
              sub={counts.greyHits ? "do not clear these" : "none in the queue"}
              color={counts.greyHits ? t.status.negative : undefined}
            />
            <KpiCell
              label="Grey names"
              value={counts.greyActive || 0}
              sub="active window"
              color={counts.greyActive ? t.status.warning : undefined}
            />
            <KpiCell label="Open files" value={counts.filesOpen || 0} sub="audit · scores · ckyc" />
          </div>

          {tab === "queue" && (
            <Queue
              desk={desk}
              busy={busy}
              canAct={canAct}
              onAdd={(body) => run(() => onMutate("post", "/api/v1/living-tool/pit/requests", { userId, ...body }), "In the book")}
              onDecide={(id, body) => run(() => onMutate("patch", `/api/v1/living-tool/pit/requests/${id}`, { userId, ...body }), body.status === "cleared" ? "Cleared" : "Rejected")}
            />
          )}
          {tab === "grey" && (
            <Grey
              desk={desk}
              busy={busy}
              canAct={canAct}
              onAdd={(body) => run(() => onMutate("post", "/api/v1/living-tool/pit/greylist", { userId, ...body }), "On the grey list")}
              onClose={(id) => run(() => onMutate("patch", `/api/v1/living-tool/pit/greylist/${id}`, { userId }), "Window closed")}
            />
          )}
          {tab === "pack" && (
            <Pack
              desk={desk}
              canAct={canAct}
              busy={busy}
              onSave={(body) => run(() => onMutate("put", "/api/v1/living-tool/pack", { userId, ...body }), "Pack saved")}
            />
          )}
          {tab === "files" && (
            <Files
              desk={desk}
              canAct={canAct}
              busy={busy}
              onAdd={(body) => run(() => onMutate("post", "/api/v1/living-tool/files", { userId, ...body }), "File opened")}
              onPatch={(id, body) => run(() => onMutate("patch", `/api/v1/living-tool/files/${id}`, { userId, ...body }), "Closed")}
            />
          )}
        </div>
      </LivingAntd>
    </DashAntd>
  );
}

function Intake({ title, hint, children }) {
  return (
    <Sheet pad="16px 18px 8px" style={{ marginBottom: 12 }}>
      <Rule title={title} hint={hint} style={{ marginBottom: 14 }} />
      {children}
    </Sheet>
  );
}

function Queue({ desk, busy, canAct, onAdd, onDecide }) {
  const [form] = Form.useForm();
  const [filter, setFilter] = useState("pending");
  const [reject, setReject] = useState(null);
  const [reason, setReason] = useState("");
  const people = desk.people || [];

  const rows = useMemo(() => {
    const all = desk.requests || [];
    if (filter === "pending") return all.filter((r) => r.status === "pending");
    if (filter === "grey") return all.filter((r) => r.status === "pending" && r.onGreyList);
    return all;
  }, [desk.requests, filter]);

  return (
    <>
      <Intake title="New request" hint="They call. It enters here. You decide here.">
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          disabled={!canAct}
          initialValues={{ side: "buy" }}
          onFinish={async (v) => {
            const hit = people.find((p) => p.name === v.employeeName);
            const ok = await onAdd({
              employeeName: v.employeeName,
              employeeId: hit ? hit.userId : "",
              scrip: String(v.scrip || "").toUpperCase(),
              side: v.side,
              qty: v.qty,
              note: v.note || "",
            });
            if (ok) form.resetFields();
          }}
        >
          <Row gutter={[10, 0]} align="bottom">
            <Col xs={24} sm={12} lg={7}>
              <Form.Item name="employeeName" label="Employee" rules={[{ required: true, message: "Required" }]}>
                <AutoComplete
                  options={people.map((p) => ({ value: p.name, label: `${p.name} · ${p.position}` }))}
                  placeholder="Who asked"
                  filterOption={(input, opt) => String(opt.value).toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6} lg={4}>
              <Form.Item name="scrip" label="Scrip" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="RELIANCE" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <Form.Item name="side" label="Side">
                <Select options={[{ value: "buy", label: "Buy" }, { value: "sell", label: "Sell" }]} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <Form.Item name="qty" label="Qty">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={10} lg={4}>
              <Form.Item name="note" label="Note">
                <Input placeholder="Optional" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8} lg={3}>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={busy} block>Add to book</Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Intake>

      <Sheet>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px 8px" }}>
          <Micro style={{ margin: 0 }}>The queue</Micro>
          <Segmented
            size="small"
            value={filter}
            onChange={setFilter}
            options={[
              { value: "pending", label: "Pending" },
              { value: "grey", label: "Grey" },
              { value: "all", label: "All" },
            ]}
          />
        </div>
        <Table
          size="middle"
          rowKey="_id"
          showHeader
          scroll={{ x: 720 }}
          pagination={rows.length > 12 ? { pageSize: 12, size: "small", showSizeChanger: false } : false}
          dataSource={rows}
          locale={{ emptyText: <QuietEmpty>Nothing to decide. The first request starts the book.</QuietEmpty> }}
          rowClassName={(r) => (r.onGreyList && r.status === "pending" ? "lt-grey-row" : "")}
          columns={[
            {
              title: "Employee",
              dataIndex: "employeeName",
              render: (v, r) => (
                <div>
                  <div style={{ fontWeight: 650, letterSpacing: "-0.2px" }}>{v}</div>
                  {r.note ? <Text type="secondary" style={{ fontSize: 12 }}>{r.note}</Text> : null}
                </div>
              ),
            },
            {
              title: "Scrip",
              dataIndex: "scrip",
              width: 150,
              render: (v, r) => (
                <Space size={8}>
                  <span style={{ fontWeight: 700, letterSpacing: "0.02em" }}>{v}</span>
                  {r.onGreyList ? <GreyMark /> : null}
                </Space>
              ),
            },
            { title: "Side", dataIndex: "side", width: 88, render: (v) => <SideMark side={v} /> },
            {
              title: "Qty",
              dataIndex: "qty",
              width: 80,
              render: (v) => <span style={{ fontVariantNumeric: "tabular-nums" }}>{v == null ? "—" : v}</span>,
            },
            { title: "In", dataIndex: "requestedOn", width: 96, render: prettyDay },
            {
              title: "",
              width: 188,
              align: "right",
              render: (_, r) => {
                if (r.status !== "pending") return <StatusInk status={r.status} />;
                if (!canAct) return null;
                return (
                  <Space>
                    <Button type="primary" size="small" loading={busy} onClick={() => onDecide(r._id, { status: "cleared" })}>Clear</Button>
                    <Button size="small" danger loading={busy} onClick={() => { setReject(r); setReason(""); }}>Reject</Button>
                  </Space>
                );
              },
            },
          ]}
        />
      </Sheet>

      <Modal
        title="Reject request"
        open={!!reject}
        onCancel={() => setReject(null)}
        okText="Reject"
        okButtonProps={{ danger: true, loading: busy }}
        onOk={async () => {
          const ok = await onDecide(reject._id, { status: "rejected", decisionNote: reason });
          if (ok) { setReject(null); setReason(""); }
        }}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 10 }}>
          {reject ? `${reject.employeeName} · ${reject.scrip}` : ""}
        </Text>
        <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why this cannot go through" />
      </Modal>
    </>
  );
}

function Grey({ desk, busy, canAct, onAdd, onClose }) {
  const [form] = Form.useForm();
  const rows = desk.grey || [];
  return (
    <>
      <Intake title="Add a name" hint="Pending requests on this scrip flag in the queue at once.">
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          disabled={!canAct}
          onFinish={async (v) => {
            const ok = await onAdd({
              scrip: String(v.scrip || "").toUpperCase(),
              reason: v.reason || "",
              windowEnd: v.windowEnd ? v.windowEnd.format("YYYY-MM-DD") : "",
            });
            if (ok) form.resetFields();
          }}
        >
          <Row gutter={[10, 0]} align="bottom">
            <Col xs={24} sm={8} lg={6}>
              <Form.Item name="scrip" label="Scrip" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="TCS" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={16} lg={10}>
              <Form.Item name="reason" label="Why">
                <Input placeholder="Unpublished information" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={5}>
              <Form.Item name="windowEnd" label="Until">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={3}>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={busy} block>Add to list</Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Intake>
      <Sheet>
        <div style={{ padding: "14px 18px 8px" }}><Micro style={{ margin: 0 }}>Active window</Micro></div>
        <Table
          size="middle"
          rowKey="_id"
          pagination={false}
          scroll={{ x: 640 }}
          dataSource={rows}
          locale={{ emptyText: <QuietEmpty>Grey list is empty. The window is open.</QuietEmpty> }}
          columns={[
            { title: "Scrip", dataIndex: "scrip", width: 140, render: (v) => <span style={{ fontWeight: 700 }}>{v}</span> },
            { title: "Why", dataIndex: "reason", render: (v) => v || "—" },
            { title: "Window", width: 220, render: (_, r) => `${prettyDay(r.windowStart)} → ${r.windowEnd ? prettyDay(r.windowEnd) : "open"}` },
            {
              title: "",
              width: 140,
              align: "right",
              render: (_, r) => canAct ? (
                <Popconfirm title="Close this window?" onConfirm={() => onClose(r._id)} okText="Close">
                  <Button size="small" loading={busy}>Close window</Button>
                </Popconfirm>
              ) : null,
            },
          ]}
        />
      </Sheet>
    </>
  );
}

function packValues(src) {
  return {
    aum: src.aum,
    strategiesLive: src.strategiesLive,
    numbersNote: src.numbersNote,
    sebiAck: src.sebiAck,
    apmiAck: src.apmiAck,
    sebiFiledOn: src.sebiFiledOn ? dayjs(src.sebiFiledOn) : null,
    apmiFiledOn: src.apmiFiledOn ? dayjs(src.apmiFiledOn) : null,
    ddNote: src.ddNote,
  };
}

function Pack({ desk, canAct, busy, onSave }) {
  const src = desk.pack || {};
  const [form] = Form.useForm();
  useEffect(() => {
    form.setFieldsValue(packValues(desk.pack || {}));
  }, [form, desk.pack]);

  return (
    <Sheet pad="18px 20px 22px">
      <Rule title={prettyMonth(src.month)} hint="Numbers, strategies, acknowledgements" />
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        disabled={!canAct}
        onFinish={(v) => onSave({
          month: src.month,
          aum: v.aum || "",
          strategiesLive: v.strategiesLive || "",
          numbersNote: v.numbersNote || "",
          sebiAck: v.sebiAck || "",
          apmiAck: v.apmiAck || "",
          sebiFiledOn: v.sebiFiledOn ? v.sebiFiledOn.format("YYYY-MM-DD") : "",
          apmiFiledOn: v.apmiFiledOn ? v.apmiFiledOn.format("YYYY-MM-DD") : "",
          ddNote: v.ddNote || "",
        })}
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="aum" label="AUM / figures"><Input placeholder="From Velozity" /></Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Month"><Input value={prettyMonth(src.month)} disabled /></Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="strategiesLive" label="Strategies that went live">
              <Input.TextArea rows={4} placeholder="Ask ops. SEBI and APMI both need this." />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="numbersNote" label="Working notes">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginBottom: 8 }}>
          <Well style={{ padding: "14px 16px 4px" }}>
            <Micro style={{ marginBottom: 10 }}>SEBI</Micro>
            <Form.Item name="sebiFiledOn" label="Filed on"><DatePicker style={{ width: "100%" }} /></Form.Item>
            <Form.Item name="sebiAck" label="Acknowledgement"><Input /></Form.Item>
          </Well>
          <Well style={{ padding: "14px 16px 4px" }}>
            <Micro style={{ marginBottom: 10 }}>APMI</Micro>
            <Form.Item name="apmiFiledOn" label="Filed on"><DatePicker style={{ width: "100%" }} /></Form.Item>
            <Form.Item name="apmiAck" label="Acknowledgement"><Input /></Form.Item>
          </Well>
        </div>

        <Form.Item name="ddNote" label="Disclosure document">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={busy}>Save pack</Button>
      </Form>
    </Sheet>
  );
}

function Files({ desk, canAct, busy, onAdd, onPatch }) {
  const t = useDashTheme();
  const [form] = Form.useForm();
  const rows = desk.files || [];
  return (
    <>
      <Intake title="Open a file">
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          disabled={!canAct}
          initialValues={{ kind: "audit" }}
          onFinish={async (v) => {
            if (await onAdd(v)) {
              form.resetFields();
              form.setFieldsValue({ kind: "audit" });
            }
          }}
        >
          <Row gutter={[10, 0]} align="bottom">
            <Col xs={24} sm={8} lg={5}>
              <Form.Item name="kind" label="Type">
                <Select options={Object.entries(FILE_KIND).map(([value, label]) => ({ value, label }))} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={16} lg={10}>
              <Form.Item name="title" label="What is open" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="PMS internal audit — July" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={16} lg={6}>
              <Form.Item name="note" label="Note">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8} lg={3}>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={busy} block>Open</Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Intake>
      <Sheet>
        <Table
          size="middle"
          rowKey="_id"
          pagination={false}
          scroll={{ x: 640 }}
          dataSource={rows}
          locale={{ emptyText: <QuietEmpty>Audit, SCORES, CKYC — they sit here until closed.</QuietEmpty> }}
          columns={[
            {
              title: "Type",
              dataIndex: "kind",
              width: 140,
              render: (v) => <Micro style={{ color: t.c.textSecondary, margin: 0 }}>{FILE_KIND[v] || v}</Micro>,
            },
            {
              title: "File",
              dataIndex: "title",
              render: (v, r) => (
                <div>
                  <div style={{ fontWeight: 650 }}>{v}</div>
                  {r.note ? <Text type="secondary" style={{ fontSize: 12 }}>{r.note}</Text> : null}
                </div>
              ),
            },
            {
              title: "Status",
              dataIndex: "status",
              width: 110,
              render: (v) => (
                <span style={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: v === "waiting" ? t.status.warning : t.c.primary,
                }}>
                  {v}
                </span>
              ),
            },
            {
              title: "",
              width: 100,
              align: "right",
              render: (_, r) => canAct ? (
                <Popconfirm title="Close this file?" onConfirm={() => onPatch(r._id, { status: "closed" })}>
                  <Button size="small" loading={busy}>Close</Button>
                </Popconfirm>
              ) : null,
            },
          ]}
        />
      </Sheet>
    </>
  );
}
